"use client";

import * as React from "react";
import { CircleAlertIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button, type ButtonProps } from "./button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Checkbox } from "./checkbox";
import { Skeleton } from "./skeleton";
import {
	TooltipProvider,
	TooltipRoot,
	TooltipTrigger,
	TooltipContent,
} from "./tooltip";

type AllOptions = null;

/** A single option within a {@link MultiSelectFilter}. */
export type MultiSelectFilterOption<T extends string | number> = {
	label: string;
	value: T | T[];
	/** Optional muted subtext rendered beneath the label (e.g. a record's type and ID). */
	description?: React.ReactNode;
	/** Optional wrapper rendered around the option item (e.g. feature-flag gate, permission guard). */
	renderWrapper?: (children: React.ReactNode) => React.ReactNode;
};

type BaseMultiSelectFilterProps<T extends string | number> = {
	label: string;
	options: MultiSelectFilterOption<T>[];
	className?: string;
	/** Whether to show search input. Defaults to `false`. */
	enableSearch?: boolean;
	/** Placeholder text for search input. */
	searchPlaceholder?: string;
	/** Content to show when no options are found. */
	noOptionsText?: React.ReactNode;
	/** Alignment of the popover. */
	align?: "start" | "center" | "end";
	/** Offset of the popover from the trigger. */
	sideOffset?: number;
	/** Whether to disable the filter. */
	disabled?: boolean;
	/** Size of the trigger button. */
	triggerSize?: ButtonProps["size"];
	popoverClassName?: string;
	/** Callback when search input changes. Use with `serverSideSearch` for API-driven filtering. */
	onSearchChange?: (search: string) => void;
	/** When `true`, disables client-side filtering and expects parent to handle via `onSearchChange`. */
	serverSideSearch?: boolean;
	/** Whether data is currently loading (shows skeleton). Only used with `serverSideSearch`. */
	loading?: boolean;
	/** Number of skeleton rows to show when loading. Defaults to `3`. */
	loadingSkeletonCount?: number;
	/** Limit how many items can be selected. Pass a number for a generic message, or an object with a custom message. */
	selectionLimit?: number | { max: number; message: string };
	invalid?: boolean;
	/** When `true`, each option's label and description fill the row, for a description that sizes itself to the space it has. Defaults to `false`. */
	fullWidthOptions?: boolean;
};

/** Props for {@link MultiSelectFilter} when in multi-select mode (default). */
export type MultiSelectModeProps<T extends string | number> =
	BaseMultiSelectFilterProps<T> & {
		/** Whether to enable multi-select mode. If `false`, allows only single selection. */
		multiSelect?: true;
		selected: T[] | AllOptions;
		onChange: (selected: T[] | AllOptions) => void;
	};

/** Props for {@link MultiSelectFilter} when in single-select mode. */
export type SingleSelectModeProps<T extends string | number> =
	BaseMultiSelectFilterProps<T> & {
		/** Whether to enable multi-select mode. If `false`, allows only single selection. */
		multiSelect: false;
		selected: T | AllOptions;
		onChange: (selected: T | AllOptions) => void;
	};

/** Discriminated union of multi-select and single-select props. */
export type MultiSelectFilterProps<T extends string | number> =
	| MultiSelectModeProps<T>
	| SingleSelectModeProps<T>;

/**
 * Declarative filter-button component that renders a popover with checkboxes.
 *
 * Supports both multi-select (default) and single-select modes, optional search,
 * server-side search with loading skeletons, and a "clear selected" action.
 */
export function MultiSelectFilter<T extends string | number>(
	props: MultiSelectFilterProps<T>,
) {
	const {
		label,
		selected,
		options,
		onChange,
		className,
		multiSelect = true,
		enableSearch = false,
		searchPlaceholder = "Search options...",
		noOptionsText = "No options found.",
		align = "start",
		popoverClassName,
		sideOffset = 4,
		disabled = false,
		triggerSize = "sm",
		onSearchChange,
		serverSideSearch = false,
		loading = false,
		loadingSkeletonCount = 3,
		selectionLimit: selectionLimitProp,
		invalid = false,
		fullWidthOptions = false,
	} = props;

	const maxLimit =
		typeof selectionLimitProp === "object"
			? selectionLimitProp.max
			: selectionLimitProp;
	const maxMessage =
		typeof selectionLimitProp === "object"
			? selectionLimitProp.message
			: maxLimit !== undefined
				? `Maximum of ${maxLimit} selections reached.`
				: undefined;

	const [isOpen, setIsOpen] = React.useState(false);
	const [searchValue, setSearchValue] = React.useState("");

	const handleSearchChange = (value: string) => {
		setSearchValue(value);
		onSearchChange?.(value);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			setSearchValue("");
			onSearchChange?.("");
		}
	};

	const isOptionSelected = ({ value }: MultiSelectFilterOption<T>) => {
		if (selected === null) {
			return false;
		}

		if (!multiSelect && !Array.isArray(selected)) {
			if (Array.isArray(value)) {
				return value.length > 0 && value[0] === selected;
			}
			return value === selected;
		}

		const selectedArray = Array.isArray(selected) ? selected : [selected];

		if (Array.isArray(value)) {
			return value.every((v) => selectedArray.includes(v));
		}

		return selectedArray.includes(value as T);
	};

	const selectionCount =
		selected === null ? 0 : Array.isArray(selected) ? selected.length : 1;
	const isMaxReached = maxLimit !== undefined && selectionCount >= maxLimit;
	// maxLimit 0 (or negative): nothing can be selected; block opening the popover while selection is empty.
	const isTriggerDisabledByZeroMaxLimit =
		maxLimit !== undefined && maxLimit <= 0 && selectionCount === 0;

	const handleSelect = (option: MultiSelectFilterOption<T>) => {
		if (multiSelect !== false) {
			const selectedArray = Array.isArray(selected)
				? selected
				: selected !== null
					? [selected]
					: [];

			const newValueCount = Array.isArray(option.value)
				? option.value.filter((v) => !selectedArray.includes(v)).length
				: selectedArray.includes(option.value as T)
					? 0
					: 1;
			const wouldExceedLimit =
				maxLimit !== undefined &&
				selected !== null &&
				!isOptionSelected(option) &&
				selectedArray.length + newValueCount > maxLimit;
			if (wouldExceedLimit) {
				return;
			}

			if (selected !== null && isOptionSelected(option)) {
				const optionValue = option.value;
				const selectedWithoutItem = Array.isArray(optionValue)
					? selectedArray.filter((v) => !optionValue.includes(v))
					: selectedArray.filter((v) => v !== optionValue);

				if (selectedWithoutItem.length === 0) {
					(onChange as (selected: T[] | AllOptions) => void)(null);
					return;
				}

				(onChange as (selected: T[] | AllOptions) => void)(selectedWithoutItem);
			} else {
				const selectedWithItem = Array.isArray(option.value)
					? Array.from(new Set([...selectedArray, ...option.value]))
					: [...selectedArray, option.value];

				(onChange as (selected: T[] | AllOptions) => void)(selectedWithItem);
			}
		} else {
			if (Array.isArray(option.value)) {
				(onChange as (selected: T | AllOptions) => void)(
					option.value.length > 0 ? option.value[0] : null,
				);
			} else {
				(onChange as (selected: T | AllOptions) => void)(option.value);
			}
			setIsOpen(false);
		}
	};

	const handleAllSelect = () => {
		(onChange as (selected: T[] | T | AllOptions) => void)(null);
		if (multiSelect === false) {
			setIsOpen(false);
		}
	};

	const getSelectedCount = (): number => {
		if (selected === null) {
			return 0;
		}
		if (Array.isArray(selected)) {
			return selected.length;
		}
		return 1;
	};

	const getDisplayText = () => {
		const count = getSelectedCount();
		if (count === 0) {
			return label;
		}
		return `${label} (${count})`;
	};

	const trigger = (
		<Button
			aria-expanded={isOpen}
			aria-label={`Toggle ${label} filter`}
			className={cn("justify-between", className)}
			role="combobox"
			disabled={disabled || isTriggerDisabledByZeroMaxLimit}
			variant={
				invalid
					? "outline-danger"
					: selected !== null
						? "filter-active"
						: "outline"
			}
			size={triggerSize}
			aria-invalid={invalid || undefined}
		>
			{invalid ? <CircleAlertIcon aria-hidden="true" /> : null}
			<span className="truncate w-full text-center">{getDisplayText()}</span>
		</Button>
	);

	const popoverTrigger = isTriggerDisabledByZeroMaxLimit ? (
		<TooltipProvider delayDuration={0}>
			<TooltipRoot>
				<TooltipTrigger asChild>
					<span>
						<PopoverTrigger asChild>{trigger}</PopoverTrigger>
					</span>
				</TooltipTrigger>
				{maxMessage && (
					<TooltipContent>
						<p className="text-body-sm">{maxMessage}</p>
					</TooltipContent>
				)}
			</TooltipRoot>
		</TooltipProvider>
	) : (
		<PopoverTrigger asChild>{trigger}</PopoverTrigger>
	);

	return (
		<Popover open={isOpen} onOpenChange={handleOpenChange}>
			{popoverTrigger}
			<PopoverContent
				className={cn(
					"min-w-[200px] w-auto max-w-[400px] p-0",
					popoverClassName,
				)}
				align={align}
				sideOffset={sideOffset}
			>
				<Command className="bg-background" shouldFilter={!serverSideSearch}>
					{enableSearch && (
						<CommandInput
							placeholder={searchPlaceholder}
							className="h-9"
							value={searchValue}
							onValueChange={handleSearchChange}
						/>
					)}
					<CommandList>
						{serverSideSearch && loading ? (
							<div className="p-1">
								{Array.from({ length: loadingSkeletonCount }).map(
									(_, index) => (
										<div
											key={index}
											className="flex items-center gap-3 px-2 py-1.5"
										>
											<Skeleton className="h-4 w-4 rounded" />
											<Skeleton className="h-4 flex-1 rounded" />
										</div>
									),
								)}
							</div>
						) : (
							<>
								<CommandEmpty>{noOptionsText}</CommandEmpty>
								<CommandGroup>
									{options.map((option) => {
										const key = Array.isArray(option.value)
											? `${label}-${option.value.join(",")}`
											: `${label}-${option.value}`;

										const isSelected = isOptionSelected(option);
										const isDisabledByMax = isMaxReached && !isSelected;

										const commandItem = (
											<CommandItem
												key={key}
												value={`${option.label}|${Array.isArray(option.value) ? option.value.join(",") : option.value}`}
												onSelect={() => handleSelect(option)}
												disabled={isDisabledByMax}
												className="aria-selected:bg-transparent aria-selected:hover:bg-neutral-200"
											>
												<Checkbox
													checked={isSelected}
													className="mr-3 h-4 w-4 pointer-events-none"
													aria-hidden="true"
													tabIndex={-1}
													disabled={isDisabledByMax}
												/>
												<div
													className={cn(
														"flex min-w-0 flex-col",
														fullWidthOptions && "flex-1",
													)}
												>
													<span
														className={cn(
															"truncate",
															isDisabledByMax && "text-neutral-400",
														)}
														title={option.label}
													>
														{option.label}
													</span>
													{option.description != null && (
														<span
															className={cn(
																"truncate text-xs text-neutral-500",
																isDisabledByMax && "text-neutral-400",
															)}
														>
															{option.description}
														</span>
													)}
												</div>
											</CommandItem>
										);

										if (option.renderWrapper) {
											return (
												<React.Fragment key={key}>
													{option.renderWrapper(commandItem)}
												</React.Fragment>
											);
										}

										return commandItem;
									})}
								</CommandGroup>
							</>
						)}
					</CommandList>
					{isMaxReached && maxMessage && (
						<p className="border-t border-neutral-200 px-3 py-2 text-xs text-neutral-500">
							{maxMessage}
						</p>
					)}
					{selected !== null && (
						<button
							type="button"
							aria-label="Clear selected filter"
							onClick={handleAllSelect}
							className="w-full border-t border-neutral-200 px-3 py-2 text-left text-sm font-medium text-primary hover:bg-gray-50"
						>
							Clear selected ({getSelectedCount()})
						</button>
					)}
				</Command>
			</PopoverContent>
		</Popover>
	);
}
MultiSelectFilter.displayName = "MultiSelectFilter";
